import styled from "styled-components";

const DEFAULT_PRIMARY_COLOR = "rgb(33, 150, 243)";

// Simple utility to convert RGB color to RGBA with alpha
function setColorAlpha(color: string, alpha: number): string {
  // Handle rgb(r, g, b) format
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  // Handle hex format
  const hexMatch = color.match(/#([0-9a-fA-F]{6})/);
  if (hexMatch) {
    const hex = hexMatch[1];
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  // Fallback: try to use the color as-is and add opacity via CSS
  return color;
}

interface CollapsibleWrapperProps {
  isVerticalFormat?: boolean;
}

export const CollapsibleWrapper = styled.span.attrs({
  className: "collapsible-wrapper",
})<CollapsibleWrapperProps>`
  width: 100%;
  display: grid;
  grid-template-columns: ${(props) => (props.isVerticalFormat ? "20px 10px auto 1fr" : "auto 1fr")};
  grid-template-rows: auto auto auto;
`;

interface CollapsibleTabWrapperProps {
  isVerticalFormat?: boolean;
  theme?: { primaryColor?: string };
}

export const CollapsibleTabWrapper = styled.div.attrs({
  className: "collapsible-config-submenu",
})<CollapsibleTabWrapperProps>`
  grid-column-start: ${(props) => (props.isVerticalFormat ? "3" : "1")};
  display: inline-block;
  width: fit-content;
`;

interface TopHorizDividerProps {
  barPadding?: number;
  theme?: { primaryColor?: string };
}

export const TopHorizDivider = styled.div.attrs({
  className: "top-horiz-divider",
})<TopHorizDividerProps>`
  height: ${(props) => (props.barPadding || 8) * 2}px;

  div.bar-line {
    background-color: ${(props) => props.theme?.primaryColor || DEFAULT_PRIMARY_COLOR};
    height: 1px;
    margin: ${(props) => (props.barPadding || 8) * 2}px;
    margin-right: 0;
  }
`;

interface LeftVerticalDividerProps {
  theme?: { primaryColor?: string };
}

export const LeftVerticalDivider = styled.div.attrs({
  className: "left-vertical-divider",
})<LeftVerticalDividerProps>`
  width: 100%;
  height: 100%;
  grid-row: 1 / 3;
  border-right: ${(props) => {
    const primaryColor = props.theme?.primaryColor || DEFAULT_PRIMARY_COLOR;
    const faintColor = setColorAlpha(primaryColor, 0.2);
    return `3px solid ${faintColor}`;
  }};
`;

interface BottomHorizDividerProps {
  isCollapsed?: boolean;
  barPadding?: number;
  animDuration?: string;
  isVerticalFormat?: boolean;
  theme?: { primaryColor?: string };
}

export const BottomHorizDivider = styled.div.attrs({
  className: "horiz-divider",
})<BottomHorizDividerProps>`
  width: 100%;
  height: ${(props) => (props.isCollapsed ? "0" : (props.barPadding || 8) * 2)}px;
  grid-column: ${(props) => (props.isVerticalFormat ? "3 / span 2" : "span 2")};
  margin-bottom: ${(props) => (props.isCollapsed ? "0" : props.barPadding || 8)}px;
  transition: ${(props) => `all ${props.animDuration || "0.2s"} ease-out`};

  div.bar-line {
    background-color: ${(props) => props.theme?.primaryColor || DEFAULT_PRIMARY_COLOR};
    height: 1px;
    margin: ${(props) => (props.isCollapsed ? "0" : (props.barPadding || 8) * 2)}px 0;
    width: ${(props) => (props.isCollapsed ? "0%" : "100%")};
    transition: ${(props) => `all ${props.animDuration || "0.2s"} ease-out`};
  }
`;

interface CollapsibleTextBoxProps {
  isVerticalFormat?: boolean;
  isCollapsed?: boolean;
  readOnly?: boolean;
  theme?: { primaryColor?: string };
  boxMargin?: number;
  animDuration?: string;
}

export const CollapsibleTextBox = styled.div.attrs({
  className: "spf-collapsible-text-box",
})<CollapsibleTextBoxProps>`
  grid-column: ${(props) => (props.isVerticalFormat ? "3 / span 2" : "span 2")};
  background-image: ${(props) => {
    if (props.isCollapsed || props.readOnly) return "";
    const primaryColor = props.theme?.primaryColor || DEFAULT_PRIMARY_COLOR;
    const encodedColor = encodeURIComponent(primaryColor);
    return `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='${encodedColor}' stroke-width='2' stroke-dasharray='3%2c 6' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`;
  }};
  height: ${(props) => (props.isCollapsed ? "0" : "auto")};
  padding: ${(props) =>
    props.isCollapsed ? `0 0 0 ${props.boxMargin || 10}px` : `${props.boxMargin || 10}px`};
  overflow: hidden;
  opacity: ${(props) => (props.isCollapsed ? 0 : 1)};
  transition: ${(props) => `all ${props.animDuration || "0.2s"} ease-out`};

  span {
    text-decoration: none;
  }
`;

interface ShowHideTabProps {
  isCollapsed?: boolean;
  readOnly?: boolean;
  theme?: { primaryColor?: string };
}

export const ShowHideTab = styled.div.attrs({
  className: "spf-show-hide-tab",
  tabIndex: 0,
  role: "button",
})<ShowHideTabProps>`
  font-family: "system-ui", "-apple-system", "BlinkMacSystemFont", "San Francisco", "Roboto",
    "Segoe UI", "Helvetica Neue", "SF Pro Text", "Arial", "sans-serif";
  display: block;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background-color: ${(props) => {
    const primaryColor = props.theme?.primaryColor || DEFAULT_PRIMARY_COLOR;
    const faintColor = setColorAlpha(primaryColor, 0.2);
    return props.isCollapsed ? "rgba(255,255,255,0.2)" : faintColor;
  }};
  font-weight: bold;
  font-size: 18px;
  padding: 5px 10px;
  width: fit-content;
  color: ${(props) => props.theme?.primaryColor || DEFAULT_PRIMARY_COLOR};
  cursor: pointer;
  user-select: none;

  .show-hide-icon {
    &.icon-right {
      float: right;
      margin-left: 10px;
    }
    &.icon-left {
      float: left;
      margin-right: 10px;
    }
  }

  &:hover {
    background-color: ${(props) => {
      const primaryColor = props.theme?.primaryColor || DEFAULT_PRIMARY_COLOR;
      const faintColor = setColorAlpha(primaryColor, 0.2);
      return props.isCollapsed && !props.readOnly ? "rgba(255,255,255,0.2)" : faintColor;
    }};
  }
`;
